import React from 'react';
import styled from 'styled-components';
import { ExternalLink, Github } from 'lucide-react';
import { motion } from 'framer-motion';
import { Project } from '../../types';
import { theme, Badge } from '../../styles/theme';

const ProjectCardContainer = styled(motion.div)`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border};
  overflow: hidden;
  transition: all 0.2s ease;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-3px);
  }
`;

const ProjectImage = styled.div`
  height: 280px;
  background: ${theme.colors.backgroundAlt};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${theme.colors.border};
  overflow: hidden;
`;

const ProjectImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: ${theme.colors.backgroundAlt};
`;

const ProjectContent = styled.div`
  padding: ${theme.spacing.xl};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProjectHeader = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const ProjectTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const ProjectDate = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.primary};
  font-weight: 500;
  margin-bottom: ${theme.spacing.sm};
`;

const ProjectDescription = styled.p`
  color: ${theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.lg};
  flex: 1;
`;

const TechnologiesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const ProjectActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: auto;
`;

const ActionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  transition: all 0.15s ease;
  text-decoration: none;
  border: 1px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  background: transparent;

  &:hover {
    background: ${theme.colors.primary};
    color: white;
  }

  &.secondary {
    border-color: ${theme.colors.border};
    color: ${theme.colors.textLight};

    &:hover {
      background: ${theme.colors.surface};
      border-color: ${theme.colors.primary};
      color: ${theme.colors.primary};
    }
  }
`;

const FeaturedBadge = styled(Badge)`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: ${theme.colors.primary};
  color: white;
  z-index: 10;
`;

interface ProjectCardProps {
  project: Project;
  index: number;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, index }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long'
    });
  };

  return (
    <ProjectCardContainer
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <ProjectImage>
        {project.featured && <FeaturedBadge>Featured</FeaturedBadge>}
        {project.images[0] && (
          <ProjectImg 
            src={project.images[0]} 
            alt={`${project.title} - ${project.technologies.join(', ')}`}
            loading="lazy"
            width="600"
            height="280"
          />
        )}
      </ProjectImage>
      
      <ProjectContent>
        <ProjectHeader>
          <ProjectTitle>{project.title}</ProjectTitle>
          <ProjectDate>{formatDate(project.completedDate)}</ProjectDate>
          <ProjectDescription>{project.description}</ProjectDescription>
        </ProjectHeader>

        <TechnologiesContainer>
          {project.technologies.map((tech, techIndex) => (
            <Badge key={techIndex} variant="secondary">
              {tech}
            </Badge>
          ))}
        </TechnologiesContainer>

        <ProjectActions>
          {project.liveUrl && (
            <ActionButton
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={16} />
              Live Demo
            </ActionButton>
          )}
          {project.githubUrl && (
            <ActionButton
              href={project.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="secondary"
            >
              <Github size={16} />
              Code
            </ActionButton>
          )}
        </ProjectActions>
      </ProjectContent>
    </ProjectCardContainer>
  );
};